// =============================================================================
// components/community/CommunityHeader.tsx — the community interior header from
// the reference design (IMG_5877): back chevron · community icon + name +
// dropdown chevron (tap = community info) · right icon cluster (search, bell,
// overflow). Rendered in-screen (the native header is hidden), so it owns its
// own safe-area top inset and there is no "‹ Tabs" route-name back label.
//
// NO STALE UI: only icons with real behaviors are rendered — the screen passes
// a handler per icon and omits the prop to omit the icon (e.g. no gear because
// there is no server-settings endpoint/screen in the backend today).
// =============================================================================
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import CachedImage from '../ui/CachedImage';
import { Ionicons } from '../ui/Icon';
import { colors, spacing, fonts, layout as layoutTokens } from '../../constants/theme';

export interface CommunityHeaderProps {
  title: string;
  icon?: string | null;
  /** Gradient fallback when there is no icon image. */
  iconGradient?: [string, string];
  onBack: () => void;
  /** Tap on the icon + name + chevron cluster (routes to community info). */
  onTitlePress?: () => void;
  onSearch?: () => void;
  onBell?: () => void;
  /** Gear icon (reference IMG_5877) — community settings/preferences. */
  onSettings?: () => void;
  onOverflow?: () => void;
  /** Highlights the search icon while the in-screen channel search is open. */
  searchActive?: boolean;
}

export default function CommunityHeader({
  title,
  icon,
  iconGradient = [colors.primary, colors.primaryLight],
  onBack,
  onTitlePress,
  onSearch,
  onBell,
  onSettings,
  onOverflow,
  searchActive = false,
}: CommunityHeaderProps) {
  const insets = useSafeAreaInsets();
  return (
    <View style={[styles.wrap, { paddingTop: insets.top }]}>
      <View style={styles.row}>
        <TouchableOpacity
          onPress={onBack}
          style={styles.iconBtn}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.titleCluster}
          onPress={onTitlePress}
          disabled={!onTitlePress}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`${title} — community info`}
        >
          {icon ? (
            <CachedImage source={{ uri: icon }} style={styles.avatar} />
          ) : (
            <LinearGradient colors={iconGradient} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.avatar}>
              <Text style={styles.avatarLetter}>{(title.charAt(0) || 'C').toUpperCase()}</Text>
            </LinearGradient>
          )}
          <Text style={styles.title} numberOfLines={1}>{title}</Text>
          {onTitlePress ? <Ionicons name="chevron-down" size={14} color={colors.muted} /> : null}
        </TouchableOpacity>

        <View style={styles.actions}>
          {onSearch ? (
            <TouchableOpacity
              onPress={onSearch}
              style={styles.iconBtn}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
              accessibilityRole="button"
              accessibilityLabel="Search channels"
            >
              <Ionicons name="search" size={20} color={searchActive ? colors.primary : colors.ink} />
            </TouchableOpacity>
          ) : null}
          {onBell ? (
            <TouchableOpacity
              onPress={onBell}
              style={styles.iconBtn}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
              accessibilityRole="button"
              accessibilityLabel="Notification preferences"
            >
              <Ionicons name="notifications-outline" size={20} color={colors.ink} />
            </TouchableOpacity>
          ) : null}
          {onSettings ? (
            <TouchableOpacity
              onPress={onSettings}
              style={styles.iconBtn}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
              accessibilityRole="button"
              accessibilityLabel="Community settings"
            >
              <Ionicons name="settings-outline" size={20} color={colors.ink} />
            </TouchableOpacity>
          ) : null}
          {onOverflow ? (
            <TouchableOpacity
              onPress={onOverflow}
              style={styles.iconBtn}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="More options"
            >
              <Ionicons name="ellipsis-horizontal" size={20} color={colors.ink} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { backgroundColor: colors.bg },
  row: {
    height: layoutTokens.headerHeight,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
  },
  // ≥44pt tap targets across every header control (iOS HIG).
  iconBtn: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  titleCluster: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: colors.faint,
  },
  avatarLetter: { ...fonts.bold, fontSize: 13, color: '#FFFFFF' },
  title: { ...fonts.bold, fontSize: 17, color: colors.ink, flexShrink: 1 },
  actions: { flexDirection: 'row', alignItems: 'center' },
});
